// apps/canifight/src/screens/Matchup.jsx
import { useState } from 'react';
import { FIGHTERS, STAT_KEYS } from '../data/fighters';
import styles from './Matchup.module.css';

export default function Matchup({ userStats, fighterIdx, onBack, onSave }) {
  const [showResult, setShowResult] = useState(false);
  const fighter = FIGHTERS[fighterIdx];

  const yourTotal = userStats.reduce((a, b) => a + b, 0);
  const themTotal = fighter.stats.reduce((a, b) => a + b, 0);

  // Adjust win % based on user stats vs average (60)
  const avgHuman = 60;
  const statBoost = (yourTotal - avgHuman) * 0.3;
  const adjustedWinPct = Math.max(0, Math.min(99, Math.round(fighter.winPct + statBoost)));

  // Duration based on stamina matchup
  const duration = Math.max(1, Math.round((userStats[3] + fighter.stats[3]) / 2 * 1.5));

  const outcomeColors = {
    'Death': '#ff3c3c', 'KO': '#ff8c00', 'Tap Out': '#ffd700',
    'Escape': '#00cfff', 'Tie': '#a855f7', 'Mutual Destruction': '#ff3c3c',
  };
  const outcomeEmojis = {
    'Death': '💀', 'KO': '😵', 'Tap Out': '🏳️', 'Escape': '🏃',
    'Tie': '🤝', 'Mutual Destruction': '💥',
  };

  const outcomeColor = outcomeColors[fighter.outcome] || '#ff3c3c';
  const outcomeEmoji = outcomeEmojis[fighter.outcome] || '⚔️';
  const winLabel = adjustedWinPct > 50
    ? 'YOU WIN'
    : (fighter.outcome === 'Tie' || fighter.outcome === 'Mutual Destruction')
      ? fighter.outcome.toUpperCase()
      : 'YOU LOSE';

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={onBack}>←</button>
        <div className={styles.title}>YOU VS {fighter.name.toUpperCase()}</div>
        <button className={styles.saveBtn} onClick={onSave}>Save Stats</button>
      </div>

      <div className={styles.vsBanner}>
        <div className={styles.vsFighter}>
          <span className={styles.vsEmoji}>🧑</span>
          <div className={styles.vsName}>YOU</div>
          <div className={`${styles.vsScore} ${styles.vsYouScore}`}>{yourTotal}</div>
        </div>
        <div className={styles.vsLabel}>VS</div>
        <div className={styles.vsFighter}>
          <span className={styles.vsEmoji}>{fighter.emoji}</span>
          <div className={styles.vsName}>{fighter.name}</div>
          <div className={`${styles.vsScore} ${styles.vsThemScore}`}>{themTotal}</div>
        </div>
      </div>

      <div className={styles.statsComparison}>
        <div className={styles.statHeaderRow}>
          <div className={styles.statHeaderYou}>YOU</div>
          <div />
          <div className={styles.statHeaderThem}>THEM</div>
        </div>
        {STAT_KEYS.map((key, i) => {
          const you = userStats[i];
          const them = fighter.stats[i];
          const youWins = you > them;
          const tie = you === them;
          return (
            <div key={key} className={styles.statRow}>
              <div className={styles.statBarWrap}>
                <span className={`${styles.statVal} ${youWins ? styles.win : tie ? styles.tie : styles.lose}`}>
                  {you}
                </span>
                <div className={styles.barTrack}>
                  <div className={`${styles.barFill} ${styles.youFill}`} style={{ width: `${you * 10}%` }} />
                </div>
              </div>
              <div className={styles.statName}>{key}</div>
              <div className={`${styles.statBarWrap} ${styles.themSide}`}>
                <div className={styles.barTrack}>
                  <div className={`${styles.barFill} ${styles.themFill}`} style={{ width: `${them * 10}%` }} />
                </div>
                <span className={`${styles.statVal} ${!youWins && !tie ? styles.win : tie ? styles.tie : styles.lose}`}>
                  {them}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {!showResult && (
        <div className={styles.fightBtnWrap}>
          <button className={styles.btnFight} onClick={() => setShowResult(true)}>
            ⚔ FIGHT ⚔
          </button>
        </div>
      )}

      {showResult && (
        <div className={styles.results}>
          <div className={styles.resultsTitle}>⚔ FIGHT RESULTS</div>

          <div className={styles.outcomeBig}>
            <span className={styles.outcomeEmoji}>{outcomeEmoji}</span>
            <div className={styles.outcomeResult} style={{ color: outcomeColor }}>
              {winLabel}
            </div>
            <div className={styles.outcomeSub}>{fighter.tagline}</div>
          </div>

          <div className={styles.winProbSection}>
            <div className={styles.sectionLabel}>WIN PROBABILITY</div>
            <div className={styles.winProbRow}>
              <div className={styles.winProbLabel}>Your chances</div>
              <div className={styles.winProbTrack}>
                <div className={`${styles.winProbFill} ${styles.winFill}`} style={{ width: `${adjustedWinPct}%` }} />
                <div className={`${styles.winProbFill} ${styles.loseFill}`} style={{ width: `${100 - adjustedWinPct}%` }} />
              </div>
              <div className={`${styles.winProbPct}`} style={{ color: 'var(--blue)' }}>
                {adjustedWinPct}%
              </div>
            </div>
          </div>

          <div className={styles.durationSection}>
            <div className={styles.sectionLabel}>ESTIMATED DURATION</div>
            <div className={styles.durationDisplay}>
              <span className={styles.durationNum}>{duration}</span>
              <span className={styles.durationUnit}>minutes</span>
            </div>
          </div>

          <div className={styles.narrativeGrid}>
            {[
              { label: 'HOW IT ENDS', value: fighter.outcome },
              { label: 'HOW YOU WIN', value: fighter.howYouWin || "You don't." },
              { label: 'PSYCH AFTERMATH', value: fighter.psych },
              { label: 'REMATCH VIABILITY', value: fighter.rematch },
              { label: 'TIME TO FIRST BLOOD', value: fighter.firstBlood },
              { label: 'DAMAGE TAKEN', value: fighter.damage },
            ].map(({ label, value }) => (
              <div key={label} className={styles.narrativeItem}>
                <div className={styles.narrativeLabel}>{label}</div>
                <div className={styles.narrativeValue}
                  style={value === "You don't." ? { color: 'var(--muted)' } : {}}>
                  {value}
                </div>
              </div>
            ))}
          </div>

          <button className={styles.btnFight} style={{ width: '100%', marginTop: 20 }} onClick={onBack}>
            ⚔ PICK ANOTHER OPPONENT
          </button>
          <button className={styles.btnSaveRecord} onClick={onSave}>
            💾 SAVE YOUR RECORD
          </button>
        </div>
      )}
    </div>
  );
}
